import { PropsWithChildren, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import styled from "@emotion/styled";
import {
  useAppDispatch,
  useAppSelector,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import { UnlikelyAISpinner } from "@financial-advisory/ui/components/spinners";
import {
  setAccessToken,
  setTenantId,
} from "@unlikelyai-magic/shared/data-access/api";
import { Icon } from "@unlikelyai-magic/ui/icons";
import { UnlikelyAiLogo } from "@unlikelyai-magic/ui/illustrations";
import { Spacing } from "@unlikelyai-magic/ui/layouts";
import { Heading, Paragraph } from "@unlikelyai-magic/ui/typography";

const PageContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background.default};
`;

const LoadingSpinnerContainer = styled.div`
  flex: 1;
  height: 20%;
`;

const ErrorPageContainer = styled(Spacing)`
  flex: 1;
`;

const StyledButton = styled.button`
  background-color: #b11082;
  color: ${({ theme }) => theme.colors.text.dark.default};
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  align-self: center;
  max-width: 200px;

  &:hover {
    background-color: #ce1397;
  }

  &:active {
    background-color: #391f40;
  }
`;

export const RequireAuth = ({ children }: PropsWithChildren) => {
  const [searchParams] = useSearchParams();

  const dispatch = useAppDispatch();
  const { accessToken, tenantId } = useAppSelector(({ auth }) => auth);

  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    getIdTokenClaims,
    logout,
    error: authError,
  } = useAuth0();

  useEffect(() => {
    if (!isAuthenticated) return;

    const getAccessToken = async () => {
      const accessToken = await getAccessTokenSilently();
      dispatch(setAccessToken(accessToken));
    };

    const getTenantId = async () => {
      const idTokenClaims = await getIdTokenClaims();
      const organizationId = idTokenClaims?.org_id;
      dispatch(setTenantId(organizationId));
    };

    getAccessToken();
    getTenantId();
  }, [dispatch, getAccessTokenSilently, getIdTokenClaims, isAuthenticated]);

  // Check for auth error after all hooks are called
  if (authError) {
    return (
      <PageContainer>
        <ErrorPageContainer gap="md">
          <Icon icon={UnlikelyAiLogo} size="5rem" fill="#B11082" />
          <Heading level="h1" textAlign="center">
            Unable to log in
          </Heading>
          <Paragraph textAlign="center">
            {searchParams.get("error_description")}
          </Paragraph>
          <StyledButton
            onClick={() =>
              logout({ logoutParams: { returnTo: window.location.origin } })
            }
          >
            Logout
          </StyledButton>
        </ErrorPageContainer>
      </PageContainer>
    );
  }

  const LoadingScreen = (
    <PageContainer>
      <LoadingSpinnerContainer>
        <UnlikelyAISpinner />
      </LoadingSpinnerContainer>
    </PageContainer>
  );

  if (isLoading) return LoadingScreen;

  if (!isAuthenticated) {
    loginWithRedirect();
    return LoadingScreen;
  }

  if (!(accessToken && tenantId)) return LoadingScreen;

  return children;
};
